import { AnswerType } from "../types/Answer";
import { Dependency } from "../types/Dependency";
import { QuestionType } from "../types/Question";
import { findElementsByKey } from "../components/organisms/Home/Mountain/landscapeLogic";
import { hasAsweredFunctionsByType } from "./answer";

export function getNextQuestion(
  questions: QuestionType[],
  dependencies: Dependency[],
  answer: AnswerType
): QuestionType | null {
  const dependency = dependencies.find((d) => d.id === answer.questionId);

  if (!dependency) {
    const currentQuestion = [...questions].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const currentQuestionIndex = currentQuestion.findIndex(
      (q) => q.id === answer.questionId
    );
    return currentQuestion[currentQuestionIndex + 1] ?? null;
  }

  let questionDepId: number | undefined;

  if (dependency.answers && dependency.answers.length > 0) {
    const selectedValues = Array.isArray(answer.value)
      ? answer.value
      : [answer.value];

    const normalizedSelectedValues = selectedValues
      .map((v) => (typeof v === "string" ? Number(v) : v))
      .filter((v) => v != null && !Number.isNaN(v as number));

    const matchedAnswerDep = dependency.answers.find((a) =>
      normalizedSelectedValues.includes(a.id)
    );

    questionDepId = matchedAnswerDep?.next_question?.id;

    if (matchedAnswerDep?.endAfter) {
      return null;
    }
  } else if (dependency.endAfter) {
    return null;
  }

  if (!questionDepId) {
    questionDepId = dependency?.next_question?.id;
  }

  if (questionDepId) {
    return questions.find((q) => q.id === questionDepId) ?? null;
  }

  const orderedQuestions = [...questions].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  const currentQuestionIndex = orderedQuestions.findIndex(
    (q) => q.id === answer.questionId
  );

  return orderedQuestions[currentQuestionIndex + 1] ?? null;
}

export const isMandatoryQuestionnaire = (keywords: string) => {
  return !findElementsByKey(keywords);
};

const hasLastQuestionBeenAnswered = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;

  const lastDep = dependencies.reduce<Dependency | null>((acc, d) => {
    if (!acc) return d;
    return (d.order ?? 0) > (acc.order ?? 0) ? d : acc;
  }, null);

  if (!lastDep) return false;

  return sessionAnswers.some(
    (a) =>
      a.questionId === lastDep.id &&
      (hasAsweredFunctionsByType[a.questionType]?.(a.value) ?? false)
  );
};

const hasEndAfterBeenMet = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  for (const ans of sessionAnswers) {
    const dep = dependencies.find((d) => d.id === ans.questionId);
    if (!dep) continue;

    const isAnswered =
      hasAsweredFunctionsByType[ans.questionType]?.(ans.value) ?? false;

    if (!isAnswered) continue;

    if (dep.endAfter) return true;

    if (dep.answers?.length) {
      const selectedIds = (Array.isArray(ans.value) ? ans.value : [ans.value])
        .map((v) => (typeof v === "string" ? Number(v) : v))
        .filter((v) => v != null && !Number.isNaN(v as number)) as number[];

      if (dep.answers.some((a) => selectedIds.includes(a.id) && a.endAfter)) {
        return true;
      }
    }
  }

  return false;
};

export const isQuestionnaireDone = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;

  return (
    hasEndAfterBeenMet(dependencies, sessionAnswers) ||
    hasLastQuestionBeenAnswered(dependencies, sessionAnswers)
  );
};